import type { Options, comboState, Action } from '@/types/components/combobox'

/** Return an `action` string corresponding to a `KeyboardEvent` */
export function useGetActionFromKey(event: KeyboardEvent, isOpen: boolean) {
  /** Get keys informations from event */
  const { key, altKey, ctrlKey, metaKey } = event
  /** All keys that will do the default open action. `' '` = Space key */
  const openKeys = ['ArrowDown', 'ArrowUp', 'Enter', ' ']

  // handle opening when closed
  if (!isOpen && openKeys.includes(key)) {
    return 'open'
  }

  // home and end move the selected option when open or closed
  if (key === 'Home') {
    return 'first'
  }
  if (key === 'End') {
    return 'last'
  }

  // handle typing characters when open or closed
  if (
    key === 'Backspace' ||
    key === 'Clear' ||
    (key.length === 1 && key !== ' ' && !altKey && !ctrlKey && !metaKey)
  ) {
    return 'type'
  }

  // handle keys when open
  if (isOpen) {
    if (key === 'ArrowUp' && altKey) {
      return 'closeAndSelect'
    } else if (key === 'ArrowDown' && !altKey) {
      return 'next'
    } else if (key === 'ArrowUp') {
      return 'previous'
    } else if (key === 'PageUp') {
      return 'pageup'
    } else if (key === 'PageDown') {
      return 'pagedown'
    } else if (key === 'Escape') {
      return 'close'
    } else if (key === 'Enter' || key === ' ') {
      return 'selectAndClose'
    } else if (key === 'Tab') {
      return 'selectAndClose'
    }
  }
}

/**
 * Returns next index depending on the `action` name
 * @param action action name returned by `useGetActionFromKey`. Returns active index if `null`.
 * @param optionsLength amount of options in the `listbox`
 * @param pageIncrement amount of result skipped with `pageup` & `pagedown`
 * @param activeIndex state active indec
 */
export function useGetUpdatedIndex(
  action: Action,
  optionsLength: number,
  activeIndex: number,
  pageIncrement: number = 10,
): number {
  switch (action) {
    case 'first':
      return 0
    case 'last':
      return optionsLength - 1
    case 'previous':
      return Math.max(0, activeIndex - 1)
    case 'next':
      return Math.min(optionsLength - 1, activeIndex + 1)
    case 'pageup':
      return Math.max(0, activeIndex - pageIncrement)
    case 'pagedown':
      return Math.min(optionsLength - 1, activeIndex + pageIncrement)
    default:
      return activeIndex
  }
}

/**
 * return the index of an option from an array of options, based on a search string.
 * If the filter is multiple iterations of the same letter (e.g "aaa"), then cycle through first-letter matches
 * @param options options object
 * @param searchString state `searchString`
 * @param startIndex index of the options array where to start
 * @returns index of an option or -1
 */
export function useGetIndexByLetter(
  options: Options,
  searchString: comboState['searchString'],
  startIndex = 0,
): number {
  const filter = searchString
  const orderedOptions = [...options.slice(startIndex), ...options.slice(0, startIndex)]
  const firstMatch = filterOptions(orderedOptions, filter)[0]

  // first check if there is an exact match for the typed string
  if (firstMatch) {
    return options.indexOf(firstMatch)
  }

  // if the same letter is being repeated, cycle through first-letter matches
  else if (allSameLetter(filter.split(''))) {
    const matches = filterOptions(orderedOptions, filter[0])
    return options.indexOf(matches[0])
  }

  // if no matches, return -1
  else {
    return -1
  }
}

/**
 * Filter an array of options against an input string
 * @return an array of options that begin with the filter string, case-independent
 */
function filterOptions(options: Options = [], filter: string, exclude: Options = []) {
  const filteredOptions = options.filter((option) => {
    const label = option.label.toLowerCase()
    const matches = label.indexOf(filter.toLowerCase()) === 0
    const isExcluded = exclude.findIndex((option) => option.label.toLowerCase() === label) !== -1

    return matches && !isExcluded
  })

  return filteredOptions
}

/** Returns `true` if all the char of an array the same */
function allSameLetter(array: string[]): boolean {
  return array.every((letter) => letter === array[0])
}
